import { useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Target, Trophy } from "lucide-react";

interface LessonIntroDialogProps {
  title: string;
  objectives: string[];
  duration?: number;
  points?: number;
  onComplete: () => void;
}

export const LessonIntroDialog = ({ 
  title, 
  objectives, 
  duration = 30,
  points = 50,
  onComplete 
}: LessonIntroDialogProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [open, setOpen] = useState(true);

  const handleNext = () => {
    if (currentStep < objectives.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setOpen(false);
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleClose = () => {
    setOpen(false);
    onComplete();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose} modal>
      <DialogContent className="sm:max-w-[500px] w-[90vw] max-h-[90vh] overflow-y-auto mx-auto fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] z-[200] p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl font-bold text-center leading-tight">
            {title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4 sm:py-6">
          {currentStep === 0 ? (
            <div className="space-y-4 sm:space-y-6 text-center">
              <div className="p-3 sm:p-4 bg-primary/10 rounded-full w-12 h-12 sm:w-16 sm:h-16 mx-auto flex items-center justify-center">
                <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">Welcome to the Lesson!</h3>
                <p className="text-sm sm:text-base text-gray-600">
                  This lesson will take approximately {duration} minutes to complete
                  and you can earn {points} points.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              <div className="p-3 sm:p-4 bg-secondary/10 rounded-full w-12 h-12 sm:w-16 sm:h-16 mx-auto flex items-center justify-center">
                <Target className="w-6 h-6 sm:w-8 sm:h-8 text-secondary" />
              </div>
              <div className="text-center">
                <h3 className="text-lg sm:text-xl font-semibold mb-3">
                  Learning Objective {currentStep} of {objectives.length}
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  {objectives[currentStep - 1]}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between gap-2 mt-4">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className={`${currentStep === 0 ? 'invisible' : ''} px-3 sm:px-4`}
            size="sm"
          >
            <ChevronLeft className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="text-sm">Previous</span>
          </Button>
          <Button 
            onClick={handleNext}
            size="sm"
            className="px-3 sm:px-4"
          >
            {currentStep < objectives.length - 1 ? (
              <>
                <span className="text-sm">Next</span>
                <ChevronRight className="w-4 h-4 ml-1 sm:ml-2" />
              </>
            ) : (
              <span className="text-sm">Start Lesson</span>
            )}
          </Button>
        </DialogFooter>

        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
          <div className="flex gap-1.5 sm:gap-2">
            {[0, ...Array(objectives.length).keys()].map((step) => (
              <div
                key={step}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  step === currentStep ? 'bg-primary w-3 sm:w-4' : 'bg-gray-300 w-1.5 sm:w-2'
                }`}
              />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};